import isFunction from 'lodash/isFunction';
import React, { Suspense } from 'react';
// TODO: WEBCA-2683 add middleware typing
// @ts-expect-error TS(7016) FIXME: Could not find a declaration file for module 'redu... Remove this comment to see the full error message
import { addMiddleware } from 'redux-dynamic-middlewares';
import { persistStore } from 'redux-persist';

import { type Store } from 'src/types';

import { AppContext } from '../lib/Context';
import { pipePromises } from '../lib/utils/promises';
import { registerHandlers, removeAndStopHandlers } from '../sagas/TaskManager';
import { getStore } from '../store/StoreAccessor';
import { createPersistConfig, injectReducer } from '../store/StoreAsync';
import Logger from '../tools/Logger';

import { addDomain, getDomain, getDomains } from './domainRegistry';
import { getInDomain } from './externalUtils';
import {
  type Domain,
  type DomainMap,
  type DomainName,
  type IDomainModuleConfig,
  type IDomainNode,
  type IDomainTree,
  type RequiredDomainProperties,
} from './types';

const log = Logger.child({ component: 'domains/index', level: 'debug' }, true);

const GenericLoader = (): React.ReactElement => {
  return <span />;
};

/**
 * Adapter function which reconciles available module formats into a consistent value
 *
 * @param {object} obj - the incoming module object
 * @returns {object} a normalized module object
 */
function babelInterop(obj: System.Module): Domain {
  return obj && obj.__esModule ? obj.default : obj;
}

/**
 * Stops a domain with the provided name
 *
 * @param {string} name - of the domain to stop
 */
export function stopDomain<Name extends DomainName>(name: Name): void {
  const domain = getDomain(name);
  if (!domain) {
    log.warn(`startDomain: Domain does not exist: ${name}`);
  }

  if (domain?.module) {
    domain.module.stop();
  } else {
    log.warn(`stopDomain: Domain not loaded: ${name}`);
  }
}

/**
 * Stop provided domains
 *
 * @param {Array} domains - the domains to stop
 */
export function stopDomains(domains: IDomainNode[]): void {
  domains.map(domain => stopDomain(domain.name));
}

/**
 * Starts a domain with the provided name
 *
 * @param {string} name of the domain to stop
 */
export function startDomain<Name extends DomainName>(name: Name): void {
  const domain = getDomain(name);
  if (!domain) {
    log.warn(`startDomain: Domain does not exist: ${name}`);
  }

  if (domain?.module) {
    domain.module.start(getStore());
  } else {
    log.warn(`startDomain: Domain not loaded: ${name}`);
  }
}

/**
 * Start provided domains
 *
 * @param {Array} domains - the domains to start
 */
export function startDomains(domains: IDomainNode[]): void {
  domains.map(domain => startDomain(domain.name));
}

export function getLoadedDomains(): { name: string; isStarted: boolean }[] {
  return getDomains(domain => {
    return !!domain.module && domain.module.loaded;
  }).map(domain => {
    return {
      name: domain.name,
      isStarted: !!domain?.module?.started,
    };
  });
}

/**
 * Traverse provided domains parents to determine whether or not each parent will be be loaded
 *
 * note: We can't rely on current status as we may be in the middle of a recalculation
 *
 * @param {object} domain - the domain to calculate from
 * @returns {boolean} - true if all parents should be conditionally loaded
 */
function willParentsBeLoadedAndStarted(domain: IDomainNode | IDomainTree): boolean {
  return (function traverse(domainNode): boolean {
    if (!domainNode) return true;

    const state = getStore().getState();
    if (!domainNode.cond(state)) {
      return false;
    }

    return traverse(domainNode.parent);
  })(domain.parent);
}

export function startStopDomains(): void {
  getDomains(domain => !!domain?.module).map(domain => {
    const isStarted = domain?.module?.started;
    const state = getStore().getState();
    const shouldBeStarted = domain.cond(state, isStarted) && willParentsBeLoadedAndStarted(domain);

    if (isStarted && !shouldBeStarted) {
      stopDomain(domain.name);
    } else if (!isStarted && shouldBeStarted) {
      startDomain(domain.name);
    }
  });
}

function loadUnloadedDomains(): ReturnType<typeof loadDomains> {
  const domainsToLoad = getDomains(domain => {
    const state = getStore().getState();
    return !domain.module && domain.cond(state) && willParentsBeLoadedAndStarted(domain);
  });

  return loadDomains(domainsToLoad);
}

export function recalculateDomains(): Promise<$TSFixMe> {
  return loadUnloadedDomains()
    .then(startStopDomains)
    .catch((e: $TSFixMe) => {
      throw new Error(e);
    });
}

/**
 * Domain Module blueprint used by Domain Module factory to produce
 * consistent enforceable interfaces to modules.
 */
const DomainModule: Omit<IDomainModuleConfig, RequiredDomainProperties> &
  Partial<IDomainModuleConfig> = {
  name: undefined, // name of domain module (used to resolve path)
  actions: {},
  constants: {},
  selectors: {},
  components: {}, // hash of loaded components
  utils: {},
  middleware: undefined, // middlewares that this domain will load
  reducer: {}, // reducer(s) to add to rootReducer when loaded
  persisted: false, // whether or not this domain is persisted to localStorage
  persistTransforms: [], // in case the reducer would like to add transforms for its persisted data
  // this can be an array of filters/transforms or an object of names pointing to an
  // array of filters/transforms for specific reducers
  persistedReducers: [], // in case there are multiple reducers this should be an array of the names or if
  // the reducer's name does not reflect the domain name it must be set in an array
  started: false, // once a module is loaded it is started once it's handlers and reducer have been started
  loaded: false, // whether or not this domain is already loaded
  watchers: [], // watchers to register on start for this domain and then unregister on stop
  registeredHandlers: [], // registered handlers (watchers) for this domain (added on start, removed on stop)

  runSaga(store, saga) {
    return store.runSaga(saga);
  },

  /**
   * Sets a domain as loaded and executes the domains custom `onLoad` lifecycle event.
   *
   * @param {object} store - redux store
   * @returns {Promise} - returns `onLoad()` promise
   */
  load(store) {
    if (this.loaded) {
      return Promise.resolve();
    }

    log.trace(`[${this.name}]:DomainModule.load() - loading module`);
    this.loaded = true;

    return Promise.resolve(this.onLoad(store));
  },
  /**
   * Starts a domain and then executes the domains custom `onStart` lifecycle event.
   *
   * @param {object} store - the redux store
   */
  start(store) {
    if (this.started) return;

    log.trace(`[${this.name}]:DomainModule.start() - starting module`);
    this.started = true;

    if (this.watchers.length) {
      this.registeredHandlers = registerHandlers(this.watchers);
    }

    log.trace(`[${this.name}]:DomainModule.start() - started`);
    this.onStart(store);
  },
  /**
   * Stops a domain and executes the domains custom `onStop` lifecycle event.
   */
  stop() {
    if (!this.started) return;
    log.trace(`[${this.name}]:DomainModule.stop() - stopping module`);
    this.started = false;

    if (this.watchers.length) {
      removeAndStopHandlers(this.registeredHandlers, this.name);
    }

    this.onStop();
  },
  /**
   * Custom lifecycle event for load.
   * Note: this event is only triggered once per domain
   *
   * @returns {Promise} - returns `onLoad` promise
   */
  onLoad() {
    return Promise.resolve();
  },
  onStart() {
    // post start lifecycle event for module implementors to register start events
  },
  onStop() {
    // post end lifecycle event for module implementors to de-register started events
  },
};

/**
 * Module factory used to create new modules
 *
 * @param {object} config - object used to overload/extend module blueprint
 * @returns {object} a newly created module
 */
export function createDomainModule<Name extends DomainName>(
  config: Pick<IDomainModuleConfig<Name>, RequiredDomainProperties> &
    Partial<IDomainModuleConfig<Name>>,
): DomainMap[Name] {
  return Object.assign(Object.create(DomainModule), config);
}

export function loadDomain<Name extends DomainName>(
  domainName: Name,
  store = getStore(),
  domainLoader = defaultDomainLoader,
): Promise<DomainMap[Name]> {
  log.trace(`[${domainName}]:loadDomain()`);

  // short circuit recallers
  const domain = getDomain(domainName)?.module;
  if (domain) {
    return Promise.resolve(domain);
  }

  return domainLoader(domainName, store)();
}

export function loadDomains(
  domains: IDomainNode[] | DomainName[],
  store = getStore(),
  loader = defaultDomainLoader,
): Promise<Domain[]> {
  const filteredDomains = domains.map(domain =>
    typeof domain === 'string' ? domain : domain.name,
  ) as DomainName[];

  return Promise.all(filteredDomains.map(domain => loadDomain(domain, store, loader)));
}

export function defaultDomainLoader(name: DomainName, store = getStore()): () => Promise<$TSFixMe> {
  return pipePromises(
    importDomain(name),
    injectDomainReducers(store),
    injectDomainMiddleware(),
    handleDomainPersistence(store),
    loadDomainModule(store),
    makeStartDomainFunc(store),
  );
}

/**
 * Retrieves domain from Webpack owned code split. Uses Webpacks internal module cache to
 * return loaded modules immediately and avoids refetches
 *
 * @param {string} name - the string name of the domain to load
 * @returns {object} - the loaded domain module
 */
export function importDomain<Name extends DomainName>(name: Name) {
  return (): Promise<Domain> => {
    return new Promise((resolve, reject) => {
      const domainLoader = getDomain(name);

      if (!domainLoader) {
        return reject(`importDomain(): unable to find loader for ${name}`);
      }

      return resolve(
        domainLoader.loader().then(module => {
          const domainModule = babelInterop(module) as DomainMap[Name];
          addDomain(name, domainModule);
          return domainModule;
        }),
      );
    });
  };
}

export function makeStartDomainFunc(store: Store) {
  return (domainModule: Domain) => {
    domainModule.start(store);
    return Promise.resolve(domainModule);
  };
}

/**
 * Injects the reducers of a domain module
 *
 * @param {object} store - the redux store
 * @param {Function} injector - the injector to use
 * @returns {Function} - composable function that injects a domainModules reducers
 */
export function injectDomainReducers(store: Store, injector = injectReducer) {
  return (domainModule: Domain) => {
    log.trace(`[${domainModule.name}]:injectDomainReducers() - injecting reducers`);
    const { name, reducer } = domainModule;
    if (name && reducer) {
      injector(store, name, reducer);
    }
    return Promise.resolve(domainModule);
  };
}

export function injectDomainMiddleware() {
  return (domainModule: Domain) => {
    if (domainModule.middleware) {
      addMiddleware(domainModule.middleware);
    }
    return Promise.resolve(domainModule);
  };
}

export function loadDomainModule(store: Store) {
  return (domainModule: Domain): Promise<Domain> => {
    log.trace(`[${domainModule.name}]:loadDomainModule() - loading domainModule`);
    return domainModule.load(store).then(() => {
      return domainModule;
    });
  };
}

export function handleDomainPersistence(store: Store) {
  return (domainModule: Domain) => {
    return new Promise((resolve /* , reject */) => {
      if (domainModule.persisted) {
        let reducersToPersist: string[] = [];
        if (
          Array.isArray(domainModule.persistedReducers) &&
          domainModule.persistedReducers.length
        ) {
          reducersToPersist = domainModule.persistedReducers;
        } else if (domainModule.name) {
          reducersToPersist = [domainModule.name];
        }
        log.trace(
          `[${domainModule.name}]:handleDomainPersistence() - persistence started, ${
            reducersToPersist.length
          } reducer${reducersToPersist.length === 1 ? '' : 's'} will persist`,
        );
        const resolving = reducersToPersist.map(reducerName => {
          // configure persistence for domain
          const domainPersistConfig = {
            whitelist: [reducerName],
          };
          const persistConfig = createPersistConfig(domainPersistConfig);
          domainModule.persistTransforms.forEach(transform =>
            persistConfig.transforms?.unshift(transform),
          );

          // rehydrate domain from store
          return new Promise<void>(persistSetupDone => {
            persistStore(store, persistConfig, (err /* , state */) => {
              log.trace(
                `[${domainModule.name}]:handleDomainPersistence(${reducerName}) - persistence finished`,
              );
              if (err) throw err;
              persistSetupDone();
            });
          });
        });
        Promise.all(resolving).then(() => resolve(domainModule));
      } else {
        log.trace(`[${domainModule.name}]:handleDomainPersistence() - not persisted`);
        resolve(domainModule);
      }
    });
  };
}

/**
 * HoC Component for lazy-loading other Components with optional loader
 *
 * @example
 * const importer = () => {
 *  return System.import(`${BASE_PATH}/src/containers/MyStations/MyStations`);
 * },
 * <AsyncComponent
 *   component='Button'     // relative path from components folder
 *   loader={GenericLoader} // component to use as loader (optional)
 *   importer={importer}    // a function that returns a system import that uses an absolute path
 * />
 */
interface AsyncComponentProps {
  loader: React.ComponentType<unknown>;
  importer: <Module extends { default: React.ComponentType }>() => Promise<Module>;
}

export class AsyncComponent extends React.Component<AsyncComponentProps> {
  Lazy: React.LazyExoticComponent<React.ComponentType>;

  static defaultProps = {
    loader: <GenericLoader />,
  };

  constructor(props: AsyncComponentProps) {
    super(props);
    this.Lazy = React.lazy(this.props.importer);
  }

  render() {
    const loader = !React.isValidElement(this.props.loader) ? (
      <this.props.loader />
    ) : (
      this.props.loader
    );

    return (
      <Suspense fallback={loader}>
        <this.Lazy {...this.props} />
      </Suspense>
    );
  }
}

/**
 * HoC Component for lazy-loading of Domain Modules. In addition,
 * the corresponding reducer and saga handlers will also be registered
 * and started.
 *
 * @example
 *
 * <AsyncDomain
 *    module='Search'         // relative path from modules folder
 *    component='SearchModal' // name of exported Component from Module
 *    loader={GenericLoader}  // component to use as loader (optional)
 * />
 */
interface AsyncDomainProps {
  domain: DomainName;
  component: string;
  loader?: () => React.ReactElement;
  onMount?: <Params extends Record<string, unknown>>(
    store: Store,
    router: $TSFixMe,
    params: Params,
  ) => boolean;
  onUnmount?: (store: Store) => boolean;
  [componentProps: string]: $TSFixMe;
}

interface AsyncDomainState {
  Component: React.ComponentType<unknown> | null;
}

export class AsyncDomain extends React.Component<
  React.PropsWithChildren<AsyncDomainProps>,
  AsyncDomainState
> {
  _isLoading: boolean;
  _mounted: boolean;
  module: Domain | null;

  static defaultProps = {
    onMount: () => false,
    onUnmount: () => false,
  };

  constructor(props: AsyncDomainProps, context: $TSFixMe) {
    super(props, context);
    this._isLoading = false;
    this._mounted = false;
    this.module = null;

    this.state = {
      Component: null,
    };
  }

  componentDidMount() {
    this._mounted = true;
    this.load();
  }

  componentWillUnmount() {
    this._mounted = false;
    this.stop();
    this.props?.onUnmount?.(this.context.store);
  }

  static contextType = AppContext;

  load = () => {
    this._isLoading = true;

    const ensureDomain = () => {
      return loadDomain(this.props.domain, this.context.store);
    };

    return ensureDomain().then((module: Domain) => {
      this._isLoading = false;
      this.module = module;
      const blockComponentLoad = this.props?.onMount?.(this.context.store, this.context.router, {});
      if (
        module.components &&
        (this.props.component || isFunction(this.props.children)) &&
        this._mounted &&
        !blockComponentLoad
      ) {
        const ModuleComponent = getInDomain(
          `${this.props.domain}.components.${this.props.component}`,
        );

        // TODO: WEBCA-2696 need to update getInDomain so type narrowing is easier
        this.setState({
          Component: ModuleComponent as React.ComponentType<unknown>,
        });
      }
    });
  };

  stop = () => {
    // this.module?.stop(); - uncommenting this will break the domain handlers, SPIKE: WEBCA-2911
  };

  render() {
    const { Component } = this.state;

    // component still loading
    if (this._isLoading) {
      if (this.props.loader) {
        return <this.props.loader />;
      }
      return <GenericLoader />;
    }

    // component requested
    if (Component) {
      return isFunction(this.props.children) ? (
        <Component {...this.props}>{this.props.children(this.module)}</Component>
      ) : (
        <Component {...this.props} />
      );
    } else if (isFunction(this.props.children)) {
      // custom child render method provided
      return this.props.children(this.module);
    } else if (this.props.children) {
      return this.props.children;
    }

    return null;
  }
}
